import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { DeleteChildren, UpdateProduct } from "../../../connection/products";
import { UploadFile } from "../../../connection/uploads";
import { clearProductsError, deleteProducts, loadProducts, updateProduct } from "../../../store/actions/admin/products";
import { GetDefaultProduct } from "../../../store/utils/create-event";
import { Attribute, FeatureFlagsEnum, LanguagesAbbr, Product, VisibleProduct } from "../../../types/working-model";
import { capitalize } from "../../../utils/utils";
import Icon, { ICONS, COLORS } from "../../general-ui/icon";
import EnterSelectInput from "../../general-ui/select/enter-select";
import Switch from "../../general-ui/switch/switch";
import TagSelectInput from "../../general-ui/tag-select/tag-select";
import TextInput, { Validation } from "../../general-ui/text-input/text";
import Textarea from "../../general-ui/textarea/textarea";
import ModalComponent from "../../general-ui/modal/modal";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import AddProduct from "../create/homepage/editor/products/add-product";
import { useCallback } from "react";
import classNames from "classnames";
import { Tooltip } from "../../general-ui/tooltip/tooltip";
import FileInput from "../../../utils/file-input";
import { editProductReducer, getInitialState, Actions } from "./edit-product-reducer";
import { saveProduct } from "./save-product-utils";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import DeleteProductConfirmation from "./delete-product-confirmation";
import { showAlert } from "../../general-ui/alert/alert-service";

export type WorkingProductFields = "button_text" | "product_title" | "description" | "price" | "sale_price"

interface Props {
	title: string;
	open: boolean;
	close: () => void;
	onComplete: (products: Product[]) => void;
	initialSelected: Product[];
	productToEdit: Product | null;
	replacing?: null | Product | undefined;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
}

const initialRequiredFields: { [key: string]: Validation } = {
	title: Validation.normal,
	about: Validation.normal,
	image: Validation.normal
};

const CreateProductModal: React.FC<Props> = ({
	title,
	open,
	close,
	onComplete,
	initialSelected,
	productToEdit,
	replacing,
	language,
	baseLanguage
}) => {
	const dispatch = useDispatch();

	const products = useTypedSelector(state => state.ProductsReducer.products);
	const productsError = useTypedSelector(state => state.ProductsReducer.productsError);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);

	//filter is safety check for null values
	const initializedProducts = initialSelected.map(p => p?.product).filter(p => p) as number[];
	const [selectedProducts, setSelectedProducts] = useState<number[]>(initializedProducts);
	const [uploading, setUploading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [productToReplace, setProductToReplace] = useState<Product | null | undefined>(replacing);
	const [saving, setSaving] = useState(false);
	const imageRef = useRef<HTMLInputElement | null>(null);
	const [requiredFields, setRequiredFields] = useState(initialRequiredFields);
	const [workingProduct, dispatchProduct] = useReducer(editProductReducer,
		productToEdit ? getInitialState(baseLanguage, language) : null
	);
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

	const useEventHierarchy = !!(featureFlags[FeatureFlagsEnum.event_hierarchy]);

	useEffect(() => {
		if (productToEdit) {
			dispatchProduct({ type: Actions.SetProduct, payload: { ...productToEdit, baseLanguage, language } });
		} else {
			dispatchProduct({ type: Actions.ClearProduct, payload: true });
		}

	}, [productToEdit, close, baseLanguage, language]);

	useEffect(() => {
		setProductToReplace(replacing);
	}, [replacing]);

	useEffect(() => {
		if (!open) {
			//filter satefy check for null values
			const _selectedProducts = initialSelected.map(p => p?.product).filter(p => p) as number[];
			setSelectedProducts(_selectedProducts);
		}
	}, [initialSelected, open]);

	const hasCompletedRequiredFields = () => {
		const _requiredFields = { ...requiredFields };
		if (!workingProduct?.product_title?.base) _requiredFields.title = Validation.error;
		if (!workingProduct?.description?.base) _requiredFields.about = Validation.error;
		if (!workingProduct?.images.length) _requiredFields.image = Validation.error;

		setRequiredFields(_requiredFields);
		for (const field in _requiredFields) if (_requiredFields[field] === Validation.error) return false;
		return true;
	};

	const finishedAddingProduct = async () => {
		saveProduct({
			close,
			hasCompletedRequiredFields,
			initialSelected,
			onComplete,
			productToEdit,
			selectedProducts,
			workingProduct,
			setSaving,
			dispatchProduct
		});

		// if event hierarchy flag on and replacing a child product, delete product being replaced
		if (useEventHierarchy && replacing?.module_id) {
			if (token && replacing.event_id) {
				try {
					await DeleteChildren(token, [replacing.module_id], replacing.event_id);
				} catch (e) {
					console.error(e);
					showAlert({
						message: "Error removing product",
						type: "error",
						duration: 3000
					});
				}
			}
		}
	};

	const handleFinished = () => {
		if (useEventHierarchy && replacing) {
			setDeleteConfirmationOpen(true);
		} else {
			finishedAddingProduct();
		}
	};

	function resetRequiredFields() {
		const _requiredFields = { ...requiredFields };
		for (const field in _requiredFields) _requiredFields[field] = Validation.normal;
		setRequiredFields(_requiredFields);
	}

	function cancel() {
		if (workingProduct) {
			if (productToEdit) return close();
			resetRequiredFields();
			dispatchProduct({ type: Actions.ClearProduct, payload: true });
		} else {
			// when closing, reset replacing so that the last product to get replaced isnt stored
			setProductToReplace(replacing);

			close();
		}
		setSearchTerm('');
	}

	const modalTitle = useMemo(() => {
		// display "replace product" as modal title when replacing button is clicked
		if (replacing) return "Replace product";
		if (!workingProduct) {
			return title;
		} else if ("product" in workingProduct) {
			return "Edit Product";
		} else {
			return "Add New Product";
		}
	}, [replacing, workingProduct, title]);

	async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];

		if (file && user && token) {
			setUploading(true);
			const uploaded = await UploadFile(user, token, file);
			dispatchProduct({ type: Actions.AddImage, payload: uploaded });
			setRequiredFields({ ...requiredFields, image: uploaded ? Validation.normal : Validation.error });
			setUploading(false);
		}
	}

	function removeImage(index: number) {
		return () => {
			dispatchProduct({ type: Actions.RemoveImage, payload: index });
		};
	}

	function handleSearch({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(value);
	}

	function toggleProduct(product: VisibleProduct, isOn: boolean) {
		if (!token) return;

		let selectedModules = [...selectedProducts];
		const productId = product.product;
		if (!productId) return;

		if (replacing) {
			// if clicking a product that is the replacing product, or is already in the selected products, dont do anything

			if (selectedModules.includes(productId) || replacing.product === productId) return;

			// if admin clicks more than 1 product when replacing, only replace with the most recent product
			const lastProduct = productToReplace || replacing;
			const startIndex = selectedModules.findIndex((moduleId: number) => moduleId === lastProduct.product);
			selectedModules.splice(startIndex, 1, productId);

			setProductToReplace(product);
			setSelectedProducts(selectedModules);

			return;
		}

		if (isOn) {
			const _languages = workingSession?.languages || workingEvent?.homepage?.languages || ['en'];
			UpdateProduct(token, product, _languages).then(result => {
				dispatch(updateProduct(result));
			});
			selectedModules = [...selectedModules, productId];
		} else {
			selectedModules = selectedModules.filter((module: number) => module !== productId);
		}

		setSelectedProducts(selectedModules);
	}

	function editProduct(product: Product) {
		dispatchProduct({ type: Actions.SetProduct, payload: { ...product, baseLanguage, language } });
	}

	function deleteProduct(product: Product) {
		if (product?.product && user?.active_channel && token) {
			dispatch(deleteProducts([Number(product.product)], token, user?.active_channel));
		}
	}

	function setProductTitle({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		dispatchProduct({ type: Actions.SetTitle, payload: value });
		setRequiredFields({ ...requiredFields, title: value ? Validation.normal : Validation.error });
	}

	function setBuyNowTitle({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		dispatchProduct({ type: Actions.SetBuyNowTitle, payload: value });

	}

	function setDescription({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) {
		dispatchProduct({ type: Actions.SetDescription, payload: value });
		setRequiredFields({ ...requiredFields, about: value ? Validation.normal : Validation.error });
	}

	function setPrice({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		dispatchProduct({ type: Actions.SetPrice, payload: value });
	}

	function setSalePrice({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		dispatchProduct({ type: Actions.SetSalePrice, payload: value });
	}

	function formatCurrency(key: 'price' | 'sale_price', { target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		// toLocalString to handle rounding/formatting
		dispatchProduct({ type: Actions.FormatCurrency, payload: { field: key, value: value } });
	}

	function setUseAttributes(_: unknown, isOn: boolean) {
		dispatchProduct({ type: Actions.SetUseAttributes, payload: isOn });
	}

	function setCTANewTab(_: unknown, isOn: boolean) {
		dispatchProduct({ type: Actions.SetCtaNewTab, payload: isOn });
	}

	function setBypassItemModal(_: unknown, isOn: boolean) {
		dispatchProduct({ type: Actions.SetBypassModal, payload: isOn });
	}

	function setCategories(categories: string[]) {
		dispatchProduct({ type: Actions.SetCategories, payload: categories });
	}

	function setAttributeName(index: number) {
		return (attribute: string) => {
			dispatchProduct({ type: Actions.SetAttributeName, payload: [index, attribute] });
		};
	}

	function setAttributeValue(index: number) {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			dispatchProduct({ type: Actions.SetAttributeValue, payload: [index, value] });
		};
	}

	function addNewAttribute() {
		dispatchProduct({ type: Actions.AddAttribute, payload: true });
	}

	function createNewProduct() {
		dispatchProduct({ type: Actions.NewProduct, payload: { ...GetDefaultProduct(), baseLanguage, language } });
	}

	function uploadNewImage() {
		imageRef.current?.click();
	}

	function setBuyNowLink({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		dispatchProduct({ type: Actions.SetBuyNowLink, payload: value });
	}

	useEffect(() => {
		if (token && user?.active_channel) {
			dispatch(loadProducts(token, user.active_channel));
		}
	}, [dispatch, token, user]);

	const sortedAttributesList = useMemo(() => {
		const attributeTypes = new Set<string>();
		for (const product of products) {
			product.attributes?.forEach((attribute: Attribute) => { attributeTypes.add(capitalize(attribute.type)); });
		}

		// sorting in alphabetical order
		return Array.from(attributeTypes).sort();
	}, [products]);

	const visibleProducts = useMemo(() => {
		if (products) {
			const _term = searchTerm.replace(/\s/g, '').trim().toLowerCase();
			return products.filter((product: Product) => {
				// filter visible products based on matching search term AND products that don't have a parent id (templates)
				return (product.product_title.base.replace(/\s/g, '').toLowerCase().trim().startsWith(_term)) && (!product.event_id);
			});
		} else {
			return [];
		}
	}, [products, searchTerm]);

	const closeModal = useCallback(() => {
		setProductToReplace(replacing);
		close();
	}, [close, replacing]);

	const closeWarningModal = () => dispatch(clearProductsError());

	const closeDeleteConfirmation = () => {
		setDeleteConfirmationOpen(false);
	};

	return (
		<ModalComponent
			closeable={false}
			cancellable={false}
			open={open}
			onRequestClose={closeModal}
			title={modalTitle}
			footer={(
				<>
					<button onClick={cancel} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={handleFinished} className="primary">{saving ? <WaitingIndicator /> : "Done"}</button>
				</>
			)}
		>
			{workingProduct ? (
				<div className="products-modal">
					<div className="row">
						<TextInput
							placeholder={"Enter Product Title"}
							label={"Product Title*"}
							valid={requiredFields.title}
							onBlur={() => {
								setRequiredFields({ ...requiredFields, title: (workingProduct?.product_title?.[language] || workingProduct?.product_title?.base) ? Validation.normal : Validation.error });
							}}
							onChange={setProductTitle}
							defaultValue={workingProduct.product_title?.[language] as string ?? workingProduct.product_title?.base}
							value={workingProduct.product_title?.[language] as string ?? workingProduct.product_title?.base}
						/>
						<TagSelectInput
							onChange={setCategories}
							tags={workingProduct.categories ?? []}
							label={"Categories"}
						/>
					</div>
					<Textarea
						placeholder="Describe your product"
						label="About*"
						// Keeping here incase we want to add back in later.
						// make sure to also update the edit-products.tsc modal as well
						onBlur={() => {
							setRequiredFields({ ...requiredFields, about: (workingProduct?.description?.[language] || workingProduct?.description?.base) ? Validation.normal : Validation.error });
						}}
						valid={requiredFields.about}
						defaultValue={workingProduct?.description?.[language] as string ?? workingProduct?.description?.base}
						onChange={setDescription}
					/>
					<div className={classNames(
						'product-images',
						{ error: requiredFields.image === Validation.error },
						{ ["expanded-images-container"]: workingProduct?.images.length > 2 }
					)}>
						{workingProduct.images.map((image: string, index: number) => (
							<div className="product-image" key={index} style={{ backgroundImage: `url(${image.replace(/ /g, '%20')})` }}>
								<div className="product-image-actions">
									<button className="no-style" onClick={removeImage(index)}><Icon name={ICONS.TRASH} size={14} color={COLORS.WHITE} /></button>
								</div>
							</div>
						))}
						{uploading && (
							<div className="product-image">
								<WaitingIndicator />
							</div>
						)}
						{workingProduct.images.length < 4 && !uploading && (
							<div className={classNames("upload-container", { empty: !workingProduct.images.length })}>
								<button className="add-image" onClick={uploadNewImage}>
									<Icon name={ICONS.ADD} size={16} color={COLORS.WHITE} />
									<span>Upload Photo</span>
								</button>
								<p>Accepted file types are .png, .jpeg and up to 50 MB file size</p>
							</div>
						)}
						<FileInput type="file" style={{ display: 'none' }} onChange={handleImageUpload} accept={["image/png", "image/jpeg", "image/jpg", "image/gif"]} ref={imageRef} />
					</div>
					<div className="row">
						<TextInput
							placeholder={"Enter Product Price"}
							label={"Price"}
							onChange={setPrice}
							onBlur={(e) => formatCurrency('price', e)}
							defaultValue={workingProduct.price?.base ?? ''}
							value={workingProduct.price?.base ?? ''}
							numeric
						/>
						<TextInput
							placeholder={"Enter Product Sale Price"}
							label={"Sale Price"}
							onChange={setSalePrice}
							onBlur={(e) => formatCurrency('sale_price', e)}
							defaultValue={workingProduct.sale_price?.base ?? ''}
							value={workingProduct.sale_price?.base ?? ''}
							numeric
						/>
					</div>
					<div className="row">
						<TextInput
							placeholder={"Enter Product Link URL"}
							label={"Buy Now URL"}
							onChange={setBuyNowLink}
							defaultValue={workingProduct.buy_now_link ?? ''}
						/>
						<TextInput
							placeholder={"Enter Product Link Text"}
							label={"Buy Now Button Name"}
							onChange={setBuyNowTitle}
							defaultValue={workingProduct.button_text?.[language] as string ?? workingProduct.button_text?.base}
						/>
					</div>

					<div className="field-group text switch">
						<Switch
							value={'cta-new-tab'}
							on={workingProduct.cta_new_tab !== false}
							onClick={setCTANewTab}
						/>
						<label>Open URL in new tab</label>
					</div>
					<div className="field-group text switch">
						<Switch
							value={'bypass-item-modal'}
							on={!!workingProduct.settings?.bypass_modal}
							onClick={setBypassItemModal}
						/>
						<label>Bypass item modal</label>
					</div>

					<div className="field-group switch top-border">
						<Switch
							value={'use-attributes'}
							on={workingProduct.use_attributes}
							onClick={setUseAttributes}
						/>
						<label>Product Attributes</label>
					</div>
					{workingProduct.use_attributes && (
						<div className="attributes-list">
							{workingProduct.attributes && workingProduct.attributes.length > 0 && (
								<>
									{workingProduct.attributes.map((attribute: Attribute, index: number) => (
										<div key={index} className="attribute-row">
											<EnterSelectInput
												defaultOptions={sortedAttributesList.length ? sortedAttributesList : ["Color", "Size"]}
												label="Attribute"
												placeholder="Enter attribute"
												onChange={setAttributeName(index)}
												defaultValue={attribute.type}
											/>
											<TextInput
												placeholder={"Attribute value"}
												label={"Value"}
												onChange={setAttributeValue(index)}
												defaultValue={attribute.value}
											/>
										</div>
									))}
								</>
							)}

							<button onClick={addNewAttribute} className="no-style">
								<Icon name={ICONS.ADD} size={14} color={COLORS.WHITE} />
								<span>Add Attribute</span>
							</button>
						</div>
					)}
				</div>
			) : (
				<div className="select-products">
					<div className="search-products">
						<TextInput
							onChange={handleSearch}
							placeholder={"Search Products..."}
							icon={ICONS.SEARCH}
							size={"small"}
							defaultValue={searchTerm}
							updateValue={searchTerm}
						/>
						<p className="middle">or</p>
						<button className="button small" disabled={language !== baseLanguage} onClick={createNewProduct}>
							Create Product
						</button>
						{language !== baseLanguage && (
							<div className="product-tooltip">
								<Tooltip tooltip="Products may only be created in the base language">
									<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12} />
								</Tooltip>
							</div>
						)}
					</div>
					<div className="products-list">
						{visibleProducts.map((product: VisibleProduct) => (
							<AddProduct
								selected={selectedProducts}
								onClick={toggleProduct}
								product={product}
								replacing={productToReplace}
								editProduct={editProduct}
								key={product.product}
								deleteProduct={deleteProduct}
							/>
						))}
					</div>
					<ModalComponent
						title="Could not delete"
						open={!!productsError}
						onRequestClose={closeWarningModal}
						closeable
						cancellable
					>
						{/* <p>TODO: change to warning modal</p> */}
						<p>The following product(s) must be removed from all event landing pages, custom pages, homepages, and sessions before being deleted: </p><em>{productsError}</em>
					</ModalComponent>
				</div>
			)
			}
			<DeleteProductConfirmation
				onConfirm={() => finishedAddingProduct()}
				onClose={closeDeleteConfirmation}
				open={deleteConfirmationOpen}
				replacing
			/>
		</ModalComponent >
	);
};

export default CreateProductModal;
